import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  stepIconClasses,
  styled,
  StepIcon,
} from '@mui/material';
import PlacesAutocomplete, { getLatLng, geocodeByAddress } from 'react-places-autocomplete';
import { IncomeTypeOptions, ValueConclusionOptions } from '../constants';
import { GooglePlaceLocation } from '../types';
import { truncateNumber } from 'shared/utils/numberOperations';

const StyledStepIcon = styled(StepIcon)(({ theme }) => ({
  [`&.${stepIconClasses.completed}`]: {
    color: '#4caf50', // Green color for completed steps
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    location: GooglePlaceLocation;
    approaches: string[];
    incomeType: string;
    valueConclusions: string[];
  }) => void;
};

export const CreateNewUnderwritingModal: React.FC<Props> = ({ open, onClose, onCreate }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [location, setLocation] = useState<GooglePlaceLocation>({});
  const [approaches, setApproaches] = useState<string[]>(['income']);
  const [valueConclusions, setValueConclusions] = useState<string[]>(['asIs']);
  const [incomeType, setIncomeType] = useState('residential');

  const handleAddressChange = (value: string) => {
    setLocation({ ...location, address: value });
  };

  const handleAddressSelect = async (value: string) => {
    const addressInfo: google.maps.GeocoderResult = (await geocodeByAddress(value))[0];
    const coords = await getLatLng(addressInfo);
    setLocation({
      address: value,
      addressInfo: {
        street: `${addressInfo.address_components.find((c) => c.types.includes('street_number'))?.long_name || ''} ${
          addressInfo.address_components.find((c) => c.types.includes('route'))?.long_name || ''
        }`.trim(),
        city: addressInfo.address_components.find((c) => c.types.includes('locality'))?.long_name || '',
        state:
          addressInfo.address_components.find((c) => c.types.includes('administrative_area_level_1'))?.short_name || '',
        zip: addressInfo.address_components.find((c) => c.types.includes('postal_code'))?.short_name || '',
      },
      coords: { latitude: truncateNumber(coords.lat, 5), longitude: truncateNumber(coords.lng, 5) },
    });
  };

  const handleValueConclusionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValueConclusions((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const handleIncomeTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncomeType(event.target.value);
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleCreate = () => {
    onCreate({ location, approaches, incomeType, valueConclusions });
    setActiveStep(0);
    setLocation({});
    setApproaches([]);
    setIncomeType('residential');
  };

  const handleCancel = () => {
    onClose();
    setActiveStep(0);
    setLocation({});
    setApproaches([]);
    setIncomeType('residential');
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Property</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          <Step>
            <StepLabel StepIconComponent={StyledStepIcon}>Address</StepLabel>
          </Step>
          <Step>
            <StepLabel StepIconComponent={StyledStepIcon}>Income & Value Conclusions</StepLabel>
          </Step>
        </Stepper>
        {activeStep === 0 && (
          <Box>
            <Typography gutterBottom>Enter property address</Typography>
            <PlacesAutocomplete
              value={location.address || ''}
              onChange={handleAddressChange}
              onSelect={handleAddressSelect}
              debounce={400}
            >
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div>
                  <TextField
                    fullWidth
                    {...getInputProps({
                      label: 'Address',
                      autoFocus: true,
                      margin: 'normal',
                    })}
                  />
                  <div>
                    {loading && <div>Loading...</div>}
                    {suggestions.map((suggestion) => {
                      const { key, ...other } = getSuggestionItemProps(suggestion, {
                        sx: {
                          p: 1,
                          cursor: 'pointer',
                          background: suggestion.active ? '#f5f5f5' : '#fff',
                        },
                      });
                      return (
                        <Box key={suggestion.placeId} {...other}>
                          {suggestion.description}
                        </Box>
                      );
                    })}
                  </div>
                </div>
              )}
            </PlacesAutocomplete>
          </Box>
        )}
        {activeStep === 1 && (
          <Box>
            <Typography gutterBottom>Value Conclusion Options</Typography>
            <FormGroup row>
              {ValueConclusionOptions.map((valueConclusion) => (
                <FormControlLabel
                  key={valueConclusion.value}
                  control={
                    <Checkbox
                      checked={valueConclusions.includes(valueConclusion.value)}
                      onChange={handleValueConclusionChange}
                      value={valueConclusion.value}
                    />
                  }
                  label={valueConclusion.label}
                />
              ))}
            </FormGroup>
            <Box mt={2}>
              <Typography gutterBottom>Income Type</Typography>
              <RadioGroup value={incomeType} onChange={handleIncomeTypeChange} row>
                {IncomeTypeOptions.map((type) => (
                  <FormControlLabel key={type.value} value={type.value} control={<Radio />} label={type.label} />
                ))}
              </RadioGroup>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep === 0 && (
          <>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              onClick={handleNext}
              disabled={!location.address || !location.address.trim() || location.address.split(',').length < 3}
              variant="contained"
            >
              Next
            </Button>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Button onClick={handleBack}>Back</Button>
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={!valueConclusions.length || !approaches.length || !incomeType}
            >
              Create
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
